'use strict';

class CelebritiesCarousel {
  constructor(el) {
    this.el = el;
    this.track = el.querySelector('[data-ccs-track]');
    this.prevBtn = el.querySelector('[data-ccs-prev]');
    this.nextBtn = el.querySelector('[data-ccs-next]');
    this.dotsEl = el.querySelector('[data-ccs-dots]');

    if (!this.track) return;

    this.slides = Array.from(this.track.querySelectorAll('.ccs__card'));
    if (!this.slides.length) return;

    const area = el.closest('[data-mobile-cols]');
    this.mobileCols = parseInt(area ? area.dataset.mobileCols : '1', 10) || 1;
    this.currentIndex = 0;
    this._resizeTimer = null;

    this._buildDots();
    this._bindEvents();
    this._updateControls();
  }

  get _visibleCount() {
    const w = window.innerWidth;
    if (w <= 480) return 1;
    if (w <= 767) return this.mobileCols;
    if (w <= 1023) return 2;
    return 4;
  }

  get _pageCount() {
    return Math.max(1, Math.ceil(this.slides.length / this._visibleCount));
  }

  get _currentPage() {
    return Math.floor(this.currentIndex / this._visibleCount);
  }

  _buildDots() {
    if (!this.dotsEl) return;
    const count = this._pageCount;
    this.dotsEl.innerHTML = '';
    for (let i = 0; i < count; i++) {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'ccs__dot' + (i === 0 ? ' ccs__dot--active' : '');
      btn.setAttribute('role', 'tab');
      btn.setAttribute('aria-label', 'Page ' + (i + 1));
      btn.setAttribute('aria-selected', i === 0 ? 'true' : 'false');
      btn.dataset.page = i;
      btn.addEventListener('click', () => this._goToPage(i));
      this.dotsEl.appendChild(btn);
    }
  }

  _bindEvents() {
    if (this.prevBtn) this.prevBtn.addEventListener('click', () => this._prev());
    if (this.nextBtn) this.nextBtn.addEventListener('click', () => this._next());

    this.track.addEventListener('scroll', () => this._onScroll(), { passive: true });

    let touchStartX = 0;
    this.track.addEventListener('touchstart', e => {
      touchStartX = e.touches[0].clientX;
    }, { passive: true });

    this.track.addEventListener('touchend', e => {
      const diff = touchStartX - e.changedTouches[0].clientX;
      if (Math.abs(diff) > 40) diff > 0 ? this._next() : this._prev();
    }, { passive: true });

    window.addEventListener('resize', () => {
      clearTimeout(this._resizeTimer);
      this._resizeTimer = setTimeout(() => {
        this._buildDots();
        this._updateControls();
      }, 200);
    }, { passive: true });
  }

  _onScroll() {
    const first = this.slides[0];
    if (!first) return;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const unit = first.offsetWidth + gap;
    if (unit > 0) {
      this.currentIndex = Math.round(this.track.scrollLeft / unit);
    }
    this._updateControls();
  }

  _prev() {
    this._goTo(Math.max(0, this.currentIndex - this._visibleCount));
  }

  _next() {
    const max = Math.max(0, this.slides.length - this._visibleCount);
    this._goTo(Math.min(max, this.currentIndex + this._visibleCount));
  }

  _goTo(index) {
    this.currentIndex = Math.max(0, Math.min(index, this.slides.length - 1));
    const slide = this.slides[this.currentIndex];
    if (!slide) return;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 0;
    const unit = slide.offsetWidth + gap;
    this.track.scrollTo({ left: this.currentIndex * unit, behavior: 'smooth' });
    this._updateControls();
  }

  _goToPage(page) {
    this._goTo(page * this._visibleCount);
  }

  _updateControls() {
    if (this.prevBtn) {
      this.prevBtn.disabled = this.currentIndex === 0;
    }
    if (this.nextBtn) {
      const max = Math.max(0, this.slides.length - this._visibleCount);
      this.nextBtn.disabled = this.currentIndex >= max;
    }
    if (this.dotsEl) {
      const activePage = this._currentPage;
      this.dotsEl.querySelectorAll('.ccs__dot').forEach((dot, i) => {
        const active = i === activePage;
        dot.classList.toggle('ccs__dot--active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }
  }
}

document.querySelectorAll('[data-ccs-carousel]').forEach(el => new CelebritiesCarousel(el));
